import { jenks } from "simple-statistics";

class DataHelper {
    getGeojsonUrl(city, sector) {
        if (!city) {
            return "";
        }

        if (!sector) {
            return `geojson/${city}/ukupno.geojson`;
        }

        return `geojson/${city}/${sector}.geojson`;
    }

    async getGeojsonData(url) {
        try {
            const data = await fetch(url);
            const json = await data.json();
            return json;
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    filterProperties(properties = {}) {
        const filteredProperties = {};

        Object.keys(properties).forEach((key) => {
            if (!["top", "right", "bottom", "left", "id", "id_2", "te"].includes(key)) {
                let newKey = key;
                if (key.includes("_sum") || key.includes("_sum_sum")) {
                    newKey = key.replace("_sum_sum", "").replace("_sum", "");
                }
                filteredProperties[newKey] = this.getPollutantValueFromProperties(properties, newKey);
            }
        });

        return filteredProperties;
    }

    getPollutantsSum(data) {
        const sum = {};

        data.features.forEach((feature) => {
            const properties = this.filterProperties(feature.properties);
            Object.keys(properties).forEach((key) => {
                if (!sum.hasOwnProperty(key)) {
                    sum[key] = 0;
                }
                sum[key] += this.getPollutantValueFromProperties(properties, key);
            });
        });

        return sum;
    }

    getGridPollutants(gridId, data) {
        const pollutantValues = this.filterProperties(data.features.find((feature) => feature?.properties?.id === gridId)?.properties);
        return pollutantValues;
    }

    getPollutantOptionsForDropdown(data) {
        const feature = data.features.find((feature) => !feature?.properties?.te);
        const filteredProperties = this.filterProperties(feature.properties);

        const pollutantOptions = Object.keys(filteredProperties).reduce((accumulator, key) => {
            accumulator.push({
                key,
                text: this.getOptimalPollutantName(key),
                value: key,
            })
            return accumulator;
        }, []);
        return pollutantOptions;
    }

    getPollutantValueFromProperties(properties = {}, pollutant) {
        const value = properties[pollutant] || properties[`${pollutant}_sum`] || properties[`${pollutant}_sum_sum`];

        return value ? parseFloat(value) : 0;
    }

    getPollutantValueFromFeature(feature, pollutant) {
        return this.getPollutantValueFromProperties(feature.properties, pollutant);
    }

    getMaxPollutantValue(data, pollutant) {
        const values = data.features.filter((feature) => !feature?.properties?.te).map(
            (feature) => this.getPollutantValueFromFeature(feature, pollutant)
        );

        const maxValue = Math.max(...values);
        return maxValue;
    }

    getPositionInRanges(value, breaks) {
        if (!value || !breaks || !breaks.length) {
            return 1;
        }

        for (let i = 0; i < breaks.length; i++) {
            if (value >= breaks[i] && value <= breaks[i + 1]) {
                return i + 1;
            }
        }

        return 10; // value is outside of the range
    }

    getNaturalBreaks(data, pollutant) {
        if (!pollutant || !data) {
            return [];
        }

        const values = data.features.map(
            (feature) => this.getPollutantValueFromFeature(feature, pollutant)
        ).filter(Number);

        const notZeroValues = values.filter((value) => value !== 0);

        const nClasses = Math.min(notZeroValues.length, 10);
        const breaks = jenks(values, nClasses);

        return breaks;
    }

    roundToPrecision(number, precision) {
        if (!number) {
            return number;
        }

        // Round to the specified precision
        let rounded = number.toFixed(precision);

        while (Number(rounded) === 0 && precision <= 10) {
            rounded = number.toFixed(++precision)
        }

        // If the rounded value is not 0, return it
        if (rounded !== 0) {
            return rounded;
        }

        // If the rounded value is 0, find the nearest non-zero decimal point
        const exponent = Math.floor(Math.log10(Math.abs(number)));
        const multiplier = Math.pow(10, exponent);

        return (number / multiplier).toFixed(precision) * multiplier;
    }

    getPrecision(unit, sector) {
        if (sector === "ukupno") {
            return 3;
        }

        if (unit === "kt") {
            return 0;
        }

        if (unit === "t") {
            return 1;
        }

        
        if (unit === "kg") {
            return 2;
        }

        return 3;
    }

    getOptimalPollutantValue(name, value, sector) {
        let unit = "kg";
        let val = value;
        const lowerCaseName = name.toLowerCase();

        if (["nox", "nmvoc", "sox", "nh3", "pm2.5", "pm10", "bc", "co", "pm_tsp"].includes(lowerCaseName)) {
            unit = "kt";
            val = value / 1000 / 1000;
        } else if (["pahs", "pb", "cd", "hg", "n2o", "pau"].includes(lowerCaseName)) {
            unit = "t";
            val = value / 1000;
        } else if (["pcdd/f"].includes(lowerCaseName)) {
            unit = "g";
            val = value * 1000;
        }

        const precision = this.getPrecision(unit, sector);
        val = this.roundToPrecision(val, precision)

        return {
            value: val,
            unit,
        }
    }

    getOptimalPollutantName(name) {
        const lowerCaseName = name.toLowerCase();

        switch (lowerCaseName) {
            case "nox": return "NOx";
            case "sox": return "SOx";
            case "nmvoc": return "NMVOC";
            case "pm2.5": return "PM2,5";
            case "pm_tsp": return "TSP";
            case "pcdd/f": return "PCCD/PCDF";
            case "pcb": return "PCBs";
            default:
                return name;
        }
    }

    getColorForRange(rangePosition) {
        switch (rangePosition) {
            case 1: return "rgb(27, 94, 32)";
            case 2: return "rgb(76, 175, 80)";
            case 3: return "rgb(205, 220, 57)";
            case 4: return "rgb(255, 235, 59)";
            case 5: return "rgb(255, 193, 7)";
            case 6: return "rgb(255, 152, 0)";
            case 7: return "rgb(244, 67, 54)";
            case 8: return "rgb(233, 30, 99)";
            case 9: return "rgb(197, 17, 98)";
            case 10:
            default: return "rgb(74, 20, 140)";
        }
    }
}

const dataHelper = new DataHelper();

export default dataHelper;
